'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { Settings, Bell, Shield, User, ChevronRight, Info } from 'lucide-react';

const alertRules = [
  { id: 'rule-001', name: 'Critical Sanctions Match', description: 'Alert when a monitored entity matches OFAC/EU/UN sanctions lists', enabled: true, channel: 'Email + Dashboard' },
  { id: 'rule-002', name: 'High Escalation Probability', description: 'Alert when escalation probability exceeds 60% for monitored regions', enabled: true, channel: 'Dashboard' },
  { id: 'rule-003', name: 'Maritime Route Disruption', description: 'Alert when active shipping routes are disrupted or rerouted', enabled: true, channel: 'Email + Dashboard' },
  { id: 'rule-004', name: 'Cyber IOC Match', description: 'Alert when published IOCs match monitored network exposure profile', enabled: false, channel: 'Dashboard' },
  { id: 'rule-005', name: 'Supplier Disruption', description: 'Alert when monitored Tier-1 or Tier-2 suppliers are affected by events', enabled: true, channel: 'Email + Dashboard' },
];

export default function SettingsPage() {
  const [rules, setRules] = useState(alertRules);

  const toggleRule = (id: string) => {
    setRules(prev => prev.map(r => r.id === id ? { ...r, enabled: !r.enabled } : r));
  };

  return (
    <AppLayout activeRoute="/settings">
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Settings</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Manage your alert rules, profile, and platform preferences</p>
        </div>

        {/* Beta notice */}
        <div className="card-elevated rounded-xl border border-primary/20 bg-primary/5 p-4">
          <div className="flex items-start gap-3">
            <Info size={15} className="text-primary flex-shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              <span className="text-primary font-medium">Beta platform:</span> Full settings configuration including Slack/Teams integrations, custom thresholds, and SSO are in active development. Alert rules below are functional for this beta release.
            </p>
          </div>
        </div>

        {/* Alert Rules */}
        <div className="card-elevated rounded-xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <Bell size={15} className="text-primary" />
            <p className="text-sm font-semibold text-foreground">Alert Rules</p>
          </div>
          <div className="divide-y divide-border">
            {rules.map(rule => (
              <div key={rule.id} className="p-4 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground mb-0.5">{rule.name}</p>
                  <p className="text-xs text-muted-foreground">{rule.description}</p>
                  <p className="text-2xs text-muted-foreground mt-1">Channel: {rule.channel}</p>
                </div>
                <button
                  onClick={() => toggleRule(rule.id)}
                  className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                    rule.enabled ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      rule.enabled ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Profile */}
        <div className="card-elevated rounded-xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <User size={15} className="text-primary" />
            <p className="text-sm font-semibold text-foreground">Company Profile</p>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Company Name</label>
              <input type="text" defaultValue="Acme Corp" className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Primary Industry</label>
              <input type="text" defaultValue="Manufacturing / Electronics" className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Primary Operating Regions</label>
              <input type="text" defaultValue="APAC, Europe, North America" className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring" />
            </div>
            <button className="bg-primary text-primary-foreground text-sm font-medium px-4 py-2 rounded-md hover:opacity-90 transition-all">
              Save Profile
            </button>
          </div>
        </div>

        {/* Security */}
        <div className="card-elevated rounded-xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <Shield size={15} className="text-primary" />
            <p className="text-sm font-semibold text-foreground">Security</p>
          </div>
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Change Password</p>
                <p className="text-xs text-muted-foreground">Update your account password</p>
              </div>
              <ChevronRight size={16} className="text-muted-foreground" />
            </div>
            <div className="flex items-center justify-between border-t border-border pt-3">
              <div>
                <p className="text-sm font-medium text-foreground">Two-Factor Authentication</p>
                <p className="text-xs text-muted-foreground">Coming in a future beta release</p>
              </div>
              <span className="text-2xs px-2 py-0.5 rounded border border-primary/30 text-primary font-medium">Beta</span>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
